import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { UserPlus, ShieldCheck, CheckCircle2, Ban, Trash2, Pencil, History, ChevronDown, RefreshCw, Upload, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { PageToggles } from "@/components/PageToggles";
import { AccessPresets, PresetSelect } from "@/components/AccessPresets";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  disabled: "bg-slate-200 text-slate-600",
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited (or "new")
  const [form, setForm] = useState({});
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPreset, setBulkPreset] = useState("");
  const [editorPreset, setEditorPreset] = useState("");

  const loadPresets = useCallback(() => {
    axios.get(`${API}/admin/presets`).then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    axios.get(`${API}/admin/users`).then((r) => setUsers(r.data)).catch(() => {});
    axios.get(`${API}/admin/audit-logs`).then((r) => setAudit(r.data)).catch(() => {});
    loadPresets();
    window.dispatchEvent(new Event("users-changed"));
  }, [loadPresets]);

  const toggleSelected = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectableUsers = users.filter((u) => u.role !== "admin");
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((u) => selected.has(u.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableUsers.map((u) => u.id)));

  const applyBulk = async () => {
    const p = presets.find((x) => x.id === bulkPreset);
    if (!p || selected.size === 0) return;
    try {
      const r = await axios.post(`${API}/admin/presets/${p.id}/apply`, { user_ids: [...selected] });
      toast.success(`"${p.name}" applied to ${r.data.applied.length} user${r.data.applied.length === 1 ? "" : "s"}`);
      setSelected(new Set());
      setBulkPreset("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply preset"); }
  };

  const applyInEditor = (pid) => {
    setEditorPreset(pid);
    const p = presets.find((x) => x.id === pid);
    if (p) setForm((f) => ({ ...f, allowed_pages: [...p.pages] }));
  };

  useEffect(() => {
    load();
    axios.get(`${API}/meta/pages`).then((r) => setPages(r.data)).catch(() => {});
  }, [load]);

  const pendingCount = users.filter((u) => u.status === "pending").length;

  const approve = async (u) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/approve`);
      toast.success(`${u.name || u.email} approved`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };

  const setStatus = async (u, status) => {
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, { status });
      toast.success(`${u.name || u.email} ${status === "disabled" ? "disabled" : "updated"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const openNew = () => {
    setEditorPreset("");
    setEditing("new");
    setForm({ name: "", email: "", password: "", role: "user", status: "active", allowed_pages: [], can_sync: false, can_upload: false, can_tie: false });
  };
  const openEdit = (u) => {
    setEditorPreset("");
    setEditing(u);
    setForm({ name: u.name || "", email: u.email, password: "", role: u.role, status: u.status, allowed_pages: u.allowed_pages || [], can_sync: !!u.can_sync, can_upload: !!u.can_upload, can_tie: !!u.can_tie });
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/users`, form);
        toast.success("User created");
      } else {
        const payload = { name: form.name, role: form.role, status: form.status, allowed_pages: form.allowed_pages, can_sync: !!form.can_sync, can_upload: !!form.can_upload, can_tie: !!form.can_tie };
        if (form.password) payload.password = form.password;
        await axios.patch(`${API}/admin/users/${editing.id}`, payload);
        toast.success("User updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const isAdminForm = form.role === "admin";

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">Approve registrations, set roles, and grant per-report access</p>
        </div>
        <Button onClick={openNew} data-testid="create-user-button" className="bg-sky-700 hover:bg-sky-800 text-white">
          <UserPlus className="h-4 w-4 mr-2" />Add User
        </Button>
      </div>

      {pendingCount > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800" data-testid="pending-banner">
          <strong>{pendingCount}</strong> user{pendingCount > 1 ? "s" : ""} awaiting approval.
        </div>
      )}

      <AccessPresets presets={presets} pages={pages} onChanged={loadPresets} />

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0 flex-wrap gap-3">
          <CardTitle className="text-lg font-semibold text-slate-800">All Users ({users.length})</CardTitle>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 text-sm" data-testid="bulk-preset-bar">
              <span className="text-slate-600 font-medium">{selected.size} selected</span>
              <PresetSelect presets={presets} value={bulkPreset} onChange={setBulkPreset} testId="bulk-preset-select" placeholder="Choose preset…" />
              <Button size="sm" onClick={applyBulk} disabled={!bulkPreset} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="bulk-preset-apply">Apply to selected</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-users" /></TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name / Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Report Access</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className={`border-b border-slate-100 ${selected.has(u.id) ? "bg-sky-50/40" : ""}`} data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    {u.role !== "admin" && <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggleSelected(u.id)} data-testid={`select-${u.email}`} />}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-800">{u.name || "—"}</p>
                    <p className="text-xs text-slate-400">{u.email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.role === "admin" ? "border-sky-300 text-sky-700" : "border-slate-300 text-slate-600"}>
                      {u.role === "admin" && <ShieldCheck className="h-3 w-3 mr-1" />}{u.role}
                    </Badge>
                  </TableCell>
                  <TableCell><span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_BADGE[u.status]}`}>{u.status}</span></TableCell>
                  <TableCell className="max-w-[280px]">
                    {u.role === "admin"
                      ? <span className="text-xs text-slate-400">All reports</span>
                      : (u.allowed_pages || []).length === 0
                        ? <span className="text-xs text-slate-400">No access</span>
                        : <span className="text-xs text-slate-600">{u.allowed_pages.length} report{u.allowed_pages.length > 1 ? "s" : ""}</span>}
                    {u.role !== "admin" && u.can_sync && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`user-can-sync-${u.email}`}><RefreshCw className="h-2.5 w-2.5 mr-1" />SAP sync</span>
                    )}
                    {u.role !== "admin" && u.can_upload && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-sky-100 text-sky-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`user-can-upload-${u.email}`}><Upload className="h-2.5 w-2.5 mr-1" />Upload</span>
                    )}
                    {u.role !== "admin" && u.can_tie && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold" data-testid={`user-can-tie-${u.email}`}><Scale className="h-2.5 w-2.5 mr-1" />TB tie</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {u.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => approve(u)} className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`approve-${u.email}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="h-8" data-testid={`edit-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      {u.id !== me?.id && u.status !== "disabled" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u, "disabled")} className="h-8 border-slate-300 text-slate-500 hover:bg-slate-50" data-testid={`disable-${u.email}`} title="Disable"><Ban className="h-3.5 w-3.5" /></Button>
                      )}
                      {u.id !== me?.id && (
                        <Button size="sm" variant="outline" onClick={() => remove(u)} className="h-8 border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <button type="button" onClick={() => setAuditOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-4 text-left" data-testid="audit-log-toggle">
          <div className="flex items-center gap-2.5">
            <History className="h-5 w-5 text-slate-500" />
            <div>
              <p className="text-sm font-bold text-slate-900">Activity Log</p>
              <p className="text-xs text-slate-500">Who approved, created, or changed users — and when</p>
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${auditOpen ? "rotate-180" : ""}`} />
        </button>
        {auditOpen && (
          <CardContent className="pt-0 pb-4">
            <Table data-testid="audit-log-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">When</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Admin</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Action</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Target</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((a, i) => (
                  <TableRow key={i} className="border-b border-slate-100" data-testid={`audit-row-${i}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{a.timestamp ? new Date(a.timestamp).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{a.actor_name || a.actor_email}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-800 capitalize">{a.action}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.target_name || a.target_email || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.details || "—"}</TableCell>
                  </TableRow>
                ))}
                {audit.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-6">No activity recorded yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="user-edit-dialog">
          <DialogHeader className="px-6 pt-6 pb-4"><DialogTitle>{editing === "new" ? "Add User" : `Edit ${editing?.email || ""}`}</DialogTitle>
            <DialogDescription>Set the role, approval status, and which reports this user can access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto px-6 pb-4 flex-1 min-h-0" data-testid="user-edit-scroll">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email || ""} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{editing === "new" ? "Initial password" : "Reset password (leave blank to keep)"}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing === "new" ? "Min 6 characters" : "••••••••"} data-testid="user-form-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Role</Label>
                <div className="flex gap-2">
                  {["user", "admin"].map((r) => (
                    <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                      className={`flex-1 h-9 rounded-md border text-sm font-medium capitalize ${form.role === r ? "border-sky-600 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-600"}`}
                      data-testid={`user-form-role-${r}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <div className="flex gap-2">
                  {["active", "pending", "disabled"].map((s) => (
                    <button key={s} type="button" onClick={() => setForm({ ...form, status: s })}
                      className={`flex-1 h-9 rounded-md border text-xs font-medium capitalize ${form.status === s ? "border-sky-600 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-600"}`}
                      data-testid={`user-form-status-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Report access {isAdminForm && <span className="text-xs text-slate-400 font-normal">(admins see all reports)</span>}</Label>
                {!isAdminForm && presets.length > 0 && <PresetSelect presets={presets} value={editorPreset} onChange={applyInEditor} testId="editor-preset-select" />}
              </div>
              <PageToggles pages={pages} value={form.allowed_pages || []} onChange={(v) => setForm({ ...form, allowed_pages: v })} disabled={isAdminForm} />
            </div>
            <label className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${isAdminForm ? "opacity-50 border-slate-200" : form.can_sync ? "border-emerald-400 bg-emerald-50/50 cursor-pointer" : "border-slate-200 cursor-pointer hover:bg-slate-50"}`} data-testid="user-form-can-sync-label">
              <Checkbox checked={isAdminForm || !!form.can_sync} disabled={isAdminForm} onCheckedChange={(v) => setForm({ ...form, can_sync: !!v })} className="mt-0.5" data-testid="user-form-can-sync" />
              <div>
                <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 text-emerald-600" />Can run "Sync SAP Data"</p>
                <p className="text-xs text-slate-500">Shows the Sync button in the top bar and lets this user trigger a full SAP extraction. {isAdminForm ? "Admins always can." : "SAP connection settings stay admin-only."}</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${isAdminForm ? "opacity-50 border-slate-200" : form.can_upload ? "border-sky-400 bg-sky-50/50 cursor-pointer" : "border-slate-200 cursor-pointer hover:bg-slate-50"}`} data-testid="user-form-can-upload-label">
              <Checkbox checked={isAdminForm || !!form.can_upload} disabled={isAdminForm} onCheckedChange={(v) => setForm({ ...form, can_upload: !!v })} className="mt-0.5" data-testid="user-form-can-upload" />
              <div>
                <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Upload className="h-3.5 w-3.5 text-sky-600" />Can upload data (Subcon Challan Reco)</p>
                <p className="text-xs text-slate-500">Shows the Run / upload section on the Subcontracting Challan page and lets this user run or delete reconciliations. Others see the results only. {isAdminForm ? "Admins always can." : ""}</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${isAdminForm ? "opacity-50 border-slate-200" : form.can_tie ? "border-amber-400 bg-amber-50/50 cursor-pointer" : "border-slate-200 cursor-pointer hover:bg-slate-50"}`} data-testid="user-form-can-tie-label">
              <Checkbox checked={isAdminForm || !!form.can_tie} disabled={isAdminForm} onCheckedChange={(v) => setForm({ ...form, can_tie: !!v })} className="mt-0.5" data-testid="user-form-can-tie" />
              <div>
                <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Scale className="h-3.5 w-3.5 text-amber-600" />Can choose the Trial Balance tie accounts</p>
                <p className="text-xs text-slate-500">Lets this user tick which G/L accounts count toward the AP / AR Ageing ↔ TB tie, for everyone. {isAdminForm ? "Admins always can." : ""}</p>
              </div>
            </label>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-white rounded-b-lg">
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="user-form-cancel">Cancel</Button>
            <Button onClick={save} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="user-form-save">{editing === "new" ? "Create User" : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
